// S26: Admin Users API Types and Endpoints
import { apiClient } from './client';

export interface UserListItem {
  id: string;
  full_name: string;
  email: string;
  role: 'admin' | 'user';
  country: 'USA' | 'NON_USA';
  active: boolean;
  min_required_contribution_cents: number;
  base_principal_cents: number;
  withdrawable_cents: number;
  eligible_for_earnings: boolean;
  participation_status?: 'active' | 'pending_close' | 'exited'; // S41
  created_at: string;
}

// S41: Closure Request details
export interface ClosureRequest {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'cancelled';
  initiated_by: 'user' | 'admin';
  reason?: string;
  requested_at: string;
  processed_at?: string;
  cancelled_at?: string;
  exit_effective_at?: string;
}

export interface UserDetail {
  id: string;
  account_id: number;
  email: string;
  full_name: string;
  role: 'admin' | 'user';
  country: 'USA' | 'NON_USA';
  min_required_contribution_cents: number;
  contributed_amount_cents: number;
  contributed_at: string | null;
  base_principal_cents: number;
  base_principal_initialized_at: string | null;
  reinvest_percent: number;
  profit_week_cents: number;
  withdrawable_cents: number;
  active: boolean;
  participation_status?: 'active' | 'pending_close' | 'exited'; // S41
  created_at: string;
  updated_at: string;
  eligible_for_earnings: boolean;
  eligibility_status: string;
  contribution_shortfall_cents: number;
  invited_by?: {
    id: string;
    full_name: string;
    email: string;
  };
  invited_at?: string;
  active_closure_request?: ClosureRequest; // S41
  ownership_percent: number;
  contributions_count: number;
  profit_allocations_count: number;
  withdrawal_requests_count: number;
  weekly_settlements_count: number;
}

export interface UserFilters {
  page?: number;
  per?: number;
  role?: 'admin' | 'user';
  country?: 'USA' | 'NON_USA';
  status?: 'active' | 'inactive';
  min_base_principal?: number;
  max_base_principal?: number;
  search?: string;
}

export interface UsersListResponse {
  users: UserListItem[];
  pagination: {
    current_page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
}

export interface UserDetailResponse {
  user: UserDetail;
}

export interface UserUpdateParams {
  active?: boolean;
  full_name?: string;
}

export const adminUsersEndpoints = {
  getUsers: async (filters?: UserFilters): Promise<UsersListResponse> => {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per) params.append('per', filters.per.toString());
    if (filters?.role) params.append('role', filters.role);
    if (filters?.country) params.append('country', filters.country);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.min_base_principal) params.append('min_base_principal', filters.min_base_principal.toString());
    if (filters?.max_base_principal) params.append('max_base_principal', filters.max_base_principal.toString());
    if (filters?.search) params.append('search', filters.search);

    return await apiClient.get(`/api/admin/users?${params}`);
  },

  getUser: async (id: string): Promise<UserDetailResponse> => {
    return await apiClient.get(`/api/admin/users/${id}`);
  },

  updateUser: async (id: string, updates: UserUpdateParams): Promise<UserDetailResponse> => {
    return await apiClient.patch(`/api/admin/users/${id}`, { user: updates });
  },
};
